(() => {
  if (window.OutpostArtworkPreview) return;
  const queue = [];
  let active = false;
  const maximumBytes = 45 * 1024;
  const crcTable = Uint32Array.from({length:256}, (_, index) => {
    let value = index;
    for (let bit = 0; bit < 8; bit++) value = (value >>> 1) ^ (value & 1 ? 0xedb88320 : 0);
    return value >>> 0;
  });
  // Browser canvas encoders add different colour/privacy metadata. The canvas
  // is explicitly sRGB; keep its compressed pixels unchanged and omit optional
  // metadata before sending the tiny PNG to the deliberately strict service.
  const canonicalPng = async (blob, width, height) => {
    const invalid = () => { throw new Error('Image preview encoding unavailable'); };
    if (!blob || blob.type !== 'image/png' || blob.size < 57 || blob.size > 2 * 1024 * 1024) return invalid();
    const bytes = new Uint8Array(await blob.arrayBuffer());
    if (bytes.length !== blob.size || ![137,80,78,71,13,10,26,10].every((value, index) => bytes[index] === value)) return invalid();
    const view = new DataView(bytes.buffer), parts = [bytes.slice(0, 8)];
    let offset = 8, imageData = false, dataEnded = false, chunks = 0;
    while (offset + 12 <= bytes.length && ++chunks <= 512) {
      const length = view.getUint32(offset), end = offset + length + 12;
      if (end > bytes.length) return invalid();
      const type = String.fromCharCode(...bytes.subarray(offset + 4, offset + 8));
      if (!/^[A-Za-z]{2}[A-Z][A-Za-z]$/.test(type)) return invalid();
      let crc = 0xffffffff;
      for (let index = offset + 4; index < end - 4; index++) crc = (crc >>> 8) ^ crcTable[(crc ^ bytes[index]) & 255];
      if (((crc ^ 0xffffffff) >>> 0) !== view.getUint32(end - 4)) return invalid();
      if (offset === 8 && type !== 'IHDR') return invalid();
      if (type === 'IHDR') {
        if (offset !== 8 || length !== 13 || view.getUint32(offset + 8) !== width || view.getUint32(offset + 12) !== height
          || width < 1 || height < 1 || width > 160 || height > 160 || bytes[offset + 16] !== 8 || ![2,6].includes(bytes[offset + 17])
          || bytes[offset + 18] || bytes[offset + 19] || bytes[offset + 20]) return invalid();
        parts.push(bytes.slice(offset, end));
      } else if (type === 'IDAT') {
        if (dataEnded) return invalid();
        imageData = true;
        parts.push(bytes.slice(offset, end));
      } else if (type === 'IEND') {
        if (length || !imageData || end !== bytes.length) return invalid();
        parts.push(bytes.slice(offset, end));
        return new Blob(parts, {type:'image/png'});
      } else {
        if (imageData) dataEnded = true;
        // A suggested palette is optional for truecolour. Transparency keys,
        // animation and unknown critical chunks could change the pixels.
        if (type === 'PLTE') {
          if (imageData || !length || length > 768 || length % 3) return invalid();
        } else if (/^[A-Z]/.test(type) || ['tRNS','acTL','fcTL','fdAT'].includes(type)) return invalid();
      }
      offset = end;
    }
    return invalid();
  };
  const eligible = file => Boolean(file && Number.isSafeInteger(file.size) && file.size > 0 && file.size <= 50 * 1024 * 1024 && /\.(?:png|jpe?g|webp|gif)$/i.test(file.name || '') && (!file.type || file.type === 'application/octet-stream' || /^image\/(?:png|jpeg|webp|gif)$/i.test(file.type)));
  const aborted = () => new DOMException('Preview cancelled', 'AbortError');
  const run = job => new Promise((resolve, reject) => {
    const image = document.createElement('img');
    let originalUrl, settled = false;
    const cleanup = () => {
      clearTimeout(timer);
      job.signal?.removeEventListener('abort', cancel);
      image.onload = image.onerror = null;
      image.removeAttribute('src');
      if (originalUrl) URL.revokeObjectURL(originalUrl);
    };
    const finish = (error, result) => {
      if (settled) { result?.dispose(); return; }
      settled = true;
      cleanup();
      if (error) reject(error); else resolve(result);
    };
    const cancel = () => finish(aborted());
    const timer = setTimeout(() => finish(new Error('Preview timed out')), 20000);
    job.signal?.addEventListener('abort', cancel, {once:true});
    if (job.signal?.aborted) { cancel(); return; }
    image.decoding = 'async';
    image.onerror = () => finish(new Error('Image preview unavailable'));
    image.onload = async () => {
      try {
        const width = image.naturalWidth, height = image.naturalHeight;
        if (!width || !height) throw new Error('Image dimensions unavailable');
        for (const bound of [160, 128, 96]) {
          if (settled) return;
          const scale = Math.min(1, bound / Math.max(width, height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(width * scale));
          canvas.height = Math.max(1, Math.round(height * scale));
          const context = canvas.getContext('2d', {colorSpace:'srgb', colorType:'unorm8'});
          if (!context) throw new Error('Image preview unavailable');
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          const encoded = await new Promise(resolveBlob => canvas.toBlob(resolveBlob, 'image/png'));
          const thumbnailWidth = canvas.width, thumbnailHeight = canvas.height;
          canvas.width = canvas.height = 0;
          if (settled) return;
          const blob = await canonicalPng(encoded, thumbnailWidth, thumbnailHeight);
          if (settled) return;
          if (blob.size > maximumBytes) continue;
          const url = URL.createObjectURL(blob);
          let disposed = false;
          finish(null, {blob, url, width:thumbnailWidth, height:thumbnailHeight, dispose() { if (!disposed) { disposed = true; URL.revokeObjectURL(url); } }});
          return;
        }
        throw new Error('Image preview could not be reduced');
      } catch (error) { finish(error); }
    };
    Promise.resolve().then(async () => {
      // A highly compressed PNG can have enormous decoded dimensions despite
      // fitting the upload limit. Read its fixed header before asking the browser
      // to decode; skipping this optional preview never rejects the original.
      if (/\.png$/i.test(job.file.name || '') && typeof job.file.slice === 'function') {
        const bytes = await job.file.slice(0, 24).arrayBuffer();
        if (settled) return;
        const view = new DataView(bytes);
        if (view.byteLength >= 24 && view.getUint32(0) === 0x89504e47 && view.getUint32(4) === 0x0d0a1a0a && view.getUint32(12) === 0x49484452) {
          const width = view.getUint32(16), height = view.getUint32(20);
          if (width > 16384 || height > 16384 || width * height > 24000000) throw new Error('Image dimensions exceed preview limit');
        }
      }
      if (settled) return;
      originalUrl = URL.createObjectURL(job.file); image.src = originalUrl;
    }).catch(error => finish(error));
  });
  const pump = async () => {
    if (active) return;
    active = true;
    while (queue.length) {
      const job = queue.shift();
      job.signal?.removeEventListener('abort', job.cancelQueued);
      if (job.signal?.aborted) { job.reject(aborted()); continue; }
      try { job.resolve(await run(job)); } catch (error) { job.reject(error); }
    }
    active = false;
  };
  const create = (file, {signal} = {}) => {
    if (!eligible(file)) return Promise.reject(new Error('File has no raster preview'));
    if (signal?.aborted) return Promise.reject(aborted());
    return new Promise((resolve, reject) => {
      const job = {file, signal, resolve, reject};
      job.cancelQueued = () => { const index = queue.indexOf(job); if (index >= 0) { queue.splice(index, 1); reject(aborted()); } };
      signal?.addEventListener('abort', job.cancelQueued, {once:true});
      queue.push(job); pump();
    });
  };
  const thumbnailUrl = (value, endpoint, file) => {
    try {
      const url = new URL(value), base = new URL(endpoint);
      if (base.protocol !== 'https:' || url.origin !== base.origin || url.username || url.password || url.search || url.hash || !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(file?.id || '') || !/^[a-f0-9]{64}$/.test(file?.read_key || '')) return null;
      const expected = `${base.pathname.replace(/\/$/, '')}/uploads/thumbnail/${file.id}/${file.read_key}/preview.png`;
      return url.pathname === expected ? url.href : null;
    } catch (_) { return null; }
  };
  const pendingKey = 'outpost-artwork-preview-pending:v1';
  const pendingRows = () => {
    try {
      const rows = JSON.parse(sessionStorage.getItem(pendingKey) || '{}');
      return Object.fromEntries(Object.entries(rows).filter(([id, expiry]) => /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(id) && Number.isSafeInteger(expiry) && expiry > Date.now() && expiry <= Date.now() + 60000).slice(-20));
    } catch (_) { return {}; }
  };
  const pending = id => Boolean(pendingRows()[id]);
  const markPending = id => {
    if (!/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(id || '')) return;
    try { sessionStorage.setItem(pendingKey, JSON.stringify({...pendingRows(), [id]:Date.now() + 45000})); } catch (_) {}
  };
  const clearPending = id => {
    try { const rows = pendingRows(); if (!rows[id]) return; delete rows[id]; if (Object.keys(rows).length) sessionStorage.setItem(pendingKey, JSON.stringify(rows)); else sessionStorage.removeItem(pendingKey); } catch (_) {}
  };
  window.OutpostArtworkPreview = Object.freeze({eligible, create, thumbnailUrl, maximumBytes, pending, markPending, clearPending});
})();
